import { post } from '@si/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { AlertReasons, NotificationChannel, ProjectTeamMember } from '../types/projectTeam';
import type { UserNotificationSettings } from '../types/projectTeam';
import { queryKeys } from './queryKeys';

interface Params {
  projectId: string;
  userId: string;
  alertReasons: AlertReasons;
}

function mergeChannels(
  existing: NotificationChannel | null | undefined,
  incoming: NotificationChannel | null | undefined
): NotificationChannel {
  return {
    email: incoming?.email ?? existing?.email ?? false,
    sms: incoming?.sms ?? existing?.sms ?? false,
  };
}

export function useUpdateProjectUserAlertChannels() {
  const queryClient = useQueryClient();

  const { mutate, error, isPending, isSuccess, reset } = useMutation({
    mutationFn: (params: Params) =>
      post({
        url: `/projects/${params.projectId}/team/${params.userId}/alert-reasons`,
        body: params.alertReasons,
      }),
    onMutate: async (variables) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({
        queryKey: queryKeys.getProjectUsers(variables.projectId),
      });
      await queryClient.cancelQueries({
        queryKey: queryKeys.getUserNotifications(),
      });

      // Snapshot previous values for rollback
      const previousTeam = queryClient.getQueryData(queryKeys.getProjectUsers(variables.projectId));
      const previousNotifications = queryClient.getQueryData(queryKeys.getUserNotifications());

      // Optimistically update project team cache
      queryClient.setQueryData(
        queryKeys.getProjectUsers(variables.projectId),
        (old: { projectTeamMembers: ProjectTeamMember[] } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            projectTeamMembers: old.projectTeamMembers.map((member) => {
              if (member.userId.toString() !== variables.userId) return member;
              return {
                ...member,
                alertReasons: {
                  sensorAlert: mergeChannels(
                    member.alertReasons.sensorAlert,
                    variables.alertReasons.sensorAlert
                  ),
                  gatewayOffPower: mergeChannels(
                    member.alertReasons.gatewayOffPower,
                    variables.alertReasons.gatewayOffPower
                  ),
                  weeklySummary: mergeChannels(
                    member.alertReasons.weeklySummary,
                    variables.alertReasons.weeklySummary
                  ),
                },
              };
            }),
          };
        }
      );

      // Optimistically update user notifications cache
      queryClient.setQueryData(
        queryKeys.getUserNotifications(),
        (old: UserNotificationSettings[] | undefined) => {
          if (!old) return old;
          return old.map((setting) => {
            if (setting.projectId.toString() !== variables.projectId) return setting;
            return {
              ...setting,
              notifications: {
                sensorAlert: mergeChannels(
                  setting.notifications.sensorAlert,
                  variables.alertReasons.sensorAlert
                ),
                gatewayOffPower: mergeChannels(
                  setting.notifications.gatewayOffPower,
                  variables.alertReasons.gatewayOffPower
                ),
                weeklySummary: mergeChannels(
                  setting.notifications.weeklySummary,
                  variables.alertReasons.weeklySummary
                ),
              },
            };
          });
        }
      );

      return { previousTeam, previousNotifications };
    },
    onError: (_, variables, context) => {
      // Rollback to previous values on error
      if (context?.previousTeam) {
        queryClient.setQueryData(
          queryKeys.getProjectUsers(variables.projectId),
          context.previousTeam
        );
      }
      if (context?.previousNotifications) {
        queryClient.setQueryData(queryKeys.getUserNotifications(), context.previousNotifications);
      }
    },
    onSettled: (_, __, variables) => {
      // Always refetch after mutation settles to ensure consistency
      queryClient.invalidateQueries({
        queryKey: queryKeys.getProjectUsers(variables.projectId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.getUserNotifications(),
      });
    },
  });

  const update = useCallback((params: Params) => mutate(params), [mutate]);

  return {
    update,
    error,
    isPending,
    isSuccess,
    reset,
  };
}
